import { useMemo } from 'react';
import dayjs from 'dayjs';
import isUndefined from 'lodash/isUndefined';
import isNull from 'lodash/isNull';
import isEmpty from 'lodash/isEmpty';

export const CASFields = {
  StartsAt: 'starts_at',
  DueAt: 'due_at',
  Timer: 'timer',
};

/***
 * A Custom Assignment Setting (CAS) field can be in one of 3 states on the Front-End
 *   - undefined, this means that we are inheriting from the assignment-level field
 *   - null, this means the field is empty for the CAS and is a different value for the assignment-level field
 *   - truthy value, the teacher has given the student a non-empty value if this field and it is different from the assignment-level field
 ***/
export const getStudentCASValue = ({
  field,
  parentAssignment,
  customSetting,
}) => {
  if (!customSetting || isEmpty(customSetting)) {
    return { value: parentAssignment[field], isCustomSetting: false };
  }

  if (isNull(customSetting[field])) {
    if (isNull(parentAssignment[field])) {
      return { value: null, isCustomSetting: false, field };
    }
    return { value: null, isCustomSetting: true, field };
  }

  if (isUndefined(customSetting[field])) {
    return { value: parentAssignment[field], isCustomSetting: false, field };
  }

  if (!parentAssignment[field]) {
    return { value: customSetting[field], isCustomSetting: true, field };
  }

  // Now both fields (custom setting and assignment setting) values are non-null
  switch (field) {
    case CASFields.StartsAt:
    case CASFields.DueAt:
      // Internally when `utc(false)` is called dayjs removes any timezone attached to the date
      // So both dates are tested at their UTC time
      const customValue = dayjs(customSetting[field]).utc(false);
      const assignmentValue = dayjs(parentAssignment[field]).utc(false);
      if (customValue.diff(assignmentValue, 'minute') == 0) {
        return {
          value: parentAssignment[field],
          isCustomSetting: false,
          field,
        };
      }
      break;
    default:
      if (customSetting[field] === parentAssignment[field]) {
        return {
          value: parentAssignment[field],
          isCustomSetting: false,
          field,
        };
      }
  }

  return { value: customSetting[field], isCustomSetting: true, field };
};

export const hasStudentAnyCAS = ({ assignment, customSetting }) =>
  Object.values(CASFields).some((field) => {
    const { isCustomSetting } = getStudentCASValue({
      field,
      parentAssignment: assignment,
      customSetting,
    });
    return isCustomSetting;
  });

export const hasColumnAnyCAS = ({
  field,
  students,
  assignment,
  customSettings,
}) =>
  students.some((student) => {
    const { isCustomSetting } = getStudentCASValue({
      field,
      parentAssignment: assignment,
      customSetting: customSettings?.[student.user_id],
    });
    return isCustomSetting;
  });

export const hasStartsAtWarning = ({ assignment, customSettings }) => {
  if (isEmpty(customSettings)) return false;
  if (
    isEmpty(assignment[CASFields.StartsAt]) &&
    isEmpty(assignment[CASFields.DueAt])
  ) {
    return false;
  }
  const assignmentStartsAt = dayjs(assignment[CASFields.StartsAt]).utc(false);
  const assignmentDueAt = dayjs(assignment[CASFields.DueAt]).utc(false);

  return Object.values(customSettings).some((customSetting) => {
    const startsAt = getStudentCASValue({
      field: CASFields.StartsAt,
      parentAssignment: assignment,
      customSetting,
    });
    const dueAt = getStudentCASValue({
      field: CASFields.DueAt,
      parentAssignment: assignment,
      customSetting,
    });
    if (
      (!startsAt.isCustomSetting && !dueAt.isCustomSetting) ||
      // We assume that if both custom dates are set they can't invalidate assignment timeline
      (startsAt.isCustomSetting && dueAt.isCustomSetting)
    ) {
      return false;
    }
    const customStartsAt = dayjs(customSetting[CASFields.StartsAt]).utc(false);
    const customDueAt = dayjs(customSetting[CASFields.DueAt]).utc(false);
    return (
      customStartsAt.isAfter(assignmentDueAt) ||
      customDueAt.isBefore(assignmentStartsAt)
    );
  });
};

export const useCheckConflictualDates = (field, assignment, customSetting) =>
  useMemo(() => {
    if (isEmpty(customSetting)) return false;
    const startsAt = getStudentCASValue({
      field: CASFields.StartsAt,
      parentAssignment: assignment,
      customSetting,
    });
    const dueAt = getStudentCASValue({
      field: CASFields.DueAt,
      parentAssignment: assignment,
      customSetting,
    });
    const assignmentDueAtEmpty = isEmpty(assignment[CASFields.DueAt]);
    const customStartsAtEmpty = isEmpty(customSetting[CASFields.StartsAt]);
    const assignmentStartsAtEmpty = isEmpty(assignment[CASFields.StartsAt]);
    const customDueAtEmpty = isEmpty(customSetting[CASFields.DueAt]);
    if (
      (assignmentDueAtEmpty && customStartsAtEmpty) ||
      (assignmentStartsAtEmpty && customDueAtEmpty) ||
      // We assume that if both custom dates are set they can't invalidate assignment timeline
      (startsAt.isCustomSetting && dueAt.isCustomSetting)
    ) {
      return false;
    }
    const assignmentStartsAt = dayjs(assignment[CASFields.StartsAt]).utc(false);
    const assignmentDueAt = dayjs(assignment[CASFields.DueAt]).utc(false);
    const customStartsAt = dayjs(customSetting[CASFields.StartsAt]).utc(false);
    const customDueAt = dayjs(customSetting[CASFields.DueAt]).utc(false);

    return (
      (field == CASFields.DueAt && customDueAt.isBefore(assignmentStartsAt)) ||
      (field == CASFields.StartsAt && customStartsAt.isAfter(assignmentDueAt))
    );
  }, [
    assignment[CASFields.StartsAt],
    assignment[CASFields.DueAt],
    customSetting,
  ]);
